// src/pages/ClientDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { FileText, Clock, CheckCircle2, AlertCircle, Upload, LogOut, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchSubmissions();
    }
  }, [user]);

  const fetchSubmissions = async () => {
    try {
      // Get ALL submissions (no query) to avoid index issues
      const snapshot = await getDocs(collection(db, 'submissions'));
      const allData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter in JavaScript - match by userId OR email (case-insensitive)
      const userEmail = user.email?.toLowerCase();
      const filtered = allData.filter(sub => {
        const subEmail = sub.email?.toLowerCase();
        return sub.userId === user.uid || subEmail === userEmail;
      });
      
      // Sort by date (newest first)
      filtered.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
      
      setSubmissions(filtered);
      console.log('User:', user.email, 'UID:', user.uid);
      console.log('Found submissions:', filtered.length);
      
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-800', icon: Clock, progress: 25 },
      'in-design': { label: 'In Design', color: 'bg-purple-100 text-purple-800', icon: FileText, progress: 50 },
      'draft-review': { label: 'Review', color: 'bg-orange-100 text-orange-800', icon: AlertCircle, progress: 75 },
      published: { label: 'Published', color: 'bg-green-100 text-green-800', icon: CheckCircle2, progress: 100 }
    };
    return configs[status] || configs.submitted;
  };

  if (loading || loadingSubmissions) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const latest = submissions[0];
  const config = latest ? getStatusConfig(latest.status) : null;
  const StatusIcon = config?.icon;

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <span className="text-xl font-semibold text-neutral-900">Stories for Memories</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-neutral-600">{user?.email}</span>
              <button onClick={handleLogout} className="p-2 hover:bg-neutral-100 rounded-lg">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-semibold text-neutral-900 mb-2">
          Welcome, {user?.displayName || user?.email?.split('@')[0] || 'there'}!
        </h1>
        <p className="text-neutral-600 mb-8">
          {latest ? "Here's your story status" : "Ready to share your story?"}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {latest ? (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-neutral-200">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-neutral-900 mb-1">Your Story</h2>
                    <p className="text-sm text-neutral-600">
                      Submitted {format(new Date(latest.createdAt), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                    {config.label}
                  </span>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Progress</span>
                    <span className="text-sm font-medium text-blue-600">{config.progress}%</span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-2.5">
                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${config.progress}%` }}></div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-6">
                  {['submitted', 'in-design', 'draft-review', 'published'].map((stage) => {
                    const stageConfig = getStatusConfig(stage);
                    const Icon = stageConfig.icon;
                    const isActive = latest.status === stage;
                    const isDone = config.progress > stageConfig.progress;
                    return (
                      <div key={stage} className="text-center">
                        <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2 ${
                          isActive ? 'bg-blue-100 text-blue-600' : isDone ? 'bg-green-100 text-green-600' : 'bg-neutral-100 text-neutral-400'
                        }`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <p className="text-xs font-medium text-neutral-600">{stageConfig.label}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Our team will get back to you with the next steps.</strong>
                  </p>
                </div>

                {submissions.length > 1 && (
                  <div className="mt-6 pt-6 border-t border-neutral-200">
                    <h3 className="text-sm font-semibold text-neutral-900 mb-3">
                      Previous Submissions ({submissions.length - 1})
                    </h3>
                    <div className="space-y-2">
                      {submissions.slice(1).map(sub => (
                        <div key={sub.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                          <span className="text-sm text-neutral-700">
                            {format(new Date(sub.createdAt), 'MMM d, yyyy')}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusConfig(sub.status).color}`}>
                            {getStatusConfig(sub.status).label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-neutral-200">
                <FileText className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">No submissions yet</h3>
                <p className="text-neutral-600 mb-6">Start sharing your story with the world</p>
                <button 
                  onClick={() => navigate('/submit')} 
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Upload className="w-5 h-5" />
                  Submit Your Story
                </button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Quick Actions</h3>
              <button 
                onClick={() => navigate('/submit')} 
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Upload className="w-5 h-5" />
                Submit New Story
              </button>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm p-6 border border-blue-100">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">Need Help?</h3>
              <p className="text-sm text-neutral-600 mb-4">Our team is here to assist you.</p>
              <a 
                href="mailto:contactstoriesformemories@gmail.com" 
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Contact Support →
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
